import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import FormField from '../project/FormField';
import ErrorMessage from '../project/ErrorMessage';
import IconSelector from '../project/IconSelector';
import BackgroundImageSelector from '../project/BackgroundImageSelector';
import { FolderIcon, CodeBracketIcon, BuildingOfficeIcon, UserGroupIcon, XMarkIcon } from '@heroicons/react/24/outline';

const EditProjectModal = ({ project, onSave, onCancel, isLoading, error }) => {
  const { language } = useLanguage();

  // Modal açıkken scroll kilitle
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const [formData, setFormData] = useState({
    projectName: '',
    projectCode: '',
    hotelName: '',
    departmentName: '',
    projectIcon: 'icons8-maintenance-50',
    backgroundImage: 'default-bg'
  });

  const [errors, setErrors] = useState({});

  // Modal açıldığında mevcut proje verilerini doldur
  useEffect(() => {
    if (project) {
      setFormData({
        projectName: project.project_name || '',
        projectCode: project.project_code || '',
        hotelName: project.hotel_name || '',
        departmentName: project.department_name || '',
        projectIcon: project.project_icon || 'icons8-maintenance-50',
        backgroundImage: project.background_image || 'default-bg'
      });
    }
  }, [project]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.projectName.trim()) {
      newErrors.projectName = language === 'tr' ? 'Proje adı gereklidir' : 'Project name is required';
    }

    if (!formData.projectCode.trim()) {
      newErrors.projectCode = language === 'tr' ? 'Proje kodu gereklidir' : 'Project code is required';
    } else if (!/^[A-Z0-9]{2,10}$/.test(formData.projectCode)) {
      newErrors.projectCode = language === 'tr'
        ? 'Proje kodu 2-10 karakter arası büyük harf ve rakam olmalıdır'
        : 'Project code must be 2-10 characters of uppercase letters and numbers';
    }

    if (!formData.projectIcon) {
      newErrors.projectIcon = language === 'tr' ? 'Proje ikonu seçilmelidir' : 'Project icon must be selected';
    }

    if (!formData.backgroundImage) {
      newErrors.backgroundImage = language === 'tr' ? 'Arkaplan resmi seçilmelidir' : 'Background image must be selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 z-[9999] outline-none focus:outline-none">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-5xl w-full max-h-[98vh] min-h-[80vh] overflow-y-auto transform transition-all animate-in fade-in zoom-in duration-300">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {language === 'tr' ? 'Projeyi Düzenle' : 'Edit Project'}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Modal Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && <ErrorMessage message={error} />}

          {/* Icon ve Background Selection - Yan yana */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <IconSelector
              selectedIcon={formData.projectIcon}
              onIconSelect={(iconId) => handleChange('projectIcon', iconId)}
              error={errors.projectIcon}
            />

            <BackgroundImageSelector
              selectedBackground={formData.backgroundImage}
              onBackgroundSelect={(backgroundId) => handleChange('backgroundImage', backgroundId)}
              error={errors.backgroundImage}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              label={language === 'tr' ? 'Proje Adı' : 'Project Name'}
              icon={FolderIcon}
              value={formData.projectName}
              onChange={(value) => handleChange('projectName', value)}
              placeholder={language === 'tr' ? 'Proje adını girin' : 'Enter project name'}
              error={errors.projectName}
              required
            />

            <FormField
              label={language === 'tr' ? 'Proje Kodu' : 'Project Code'}
              icon={CodeBracketIcon}
              value={formData.projectCode}
              onChange={(value) => handleChange('projectCode', value.toUpperCase())}
              placeholder={language === 'tr' ? 'PROJ01' : 'PROJ01'}
              error={errors.projectCode}
              maxLength={10}
              required
            />
          </div>

          {/* Modal Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              disabled={isLoading}
            >
              {language === 'tr' ? 'İptal' : 'Cancel'}
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {language === 'tr' ? 'Güncelleniyor...' : 'Updating...'}
                </>
              ) : (
                language === 'tr' ? 'Güncelle' : 'Update'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default EditProjectModal;
